import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const sb = supabase as any;

export function useBookmarks() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: bookmarks = [], isLoading } = useQuery({
    queryKey: ["bookmarked-questions-full", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await sb
        .from("user_bookmarks")
        .select("mcq_id, created_at")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as { mcq_id: string; created_at: string }[];
    },
  });

  const bookmarkedMcqIds = new Set(bookmarks.map((b) => b.mcq_id));

  const toggleBookmark = useMutation({
    mutationFn: async (mcqId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      const isBookmarked = bookmarkedMcqIds.has(mcqId);
      if (isBookmarked) {
        const { error } = await sb
          .from("user_bookmarks")
          .delete()
          .eq("user_id", user.id)
          .eq("mcq_id", mcqId);
        if (error) throw error;
        return { action: "removed" as const };
      } else {
        const { error } = await sb
          .from("user_bookmarks")
          .insert({ user_id: user.id, mcq_id: mcqId });
        if (error) throw error;
        return { action: "added" as const };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookmarked-questions-full"] });
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      toast.success(result.action === "added" ? "Question bookmarked" : "Bookmark removed");
    },
    onError: () => {
      toast.error("Failed to update bookmark");
    },
  });

  return {
    bookmarks,
    bookmarkedMcqIds,
    isLoading,
    toggleBookmark: toggleBookmark.mutate,
    isBookmarked: (mcqId: string) => bookmarkedMcqIds.has(mcqId),
  };
}
